/**
 * The BFF's password policy, mirrored so the form can show a live checklist.
 *
 * The server is the authority — this exists so the person sees which rule is still missing while
 * typing, instead of submitting and getting one rejection at a time.
 *
 * @module Pages/Settings/UserManagement
 */

/** Special characters the BFF accepts. Anything outside this set does not satisfy the rule. */
export const PASSWORD_SPECIAL_CHARACTERS = '@$!%*?&';

export const PASSWORD_MIN_LENGTH = 8;

/** One rule of the policy, as the checklist renders it. */
export interface PasswordRule {
  id: string;
  label: string;
  isMet: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `Pelo menos ${PASSWORD_MIN_LENGTH} caracteres`,
    isMet: password => password.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'uppercase',
    label: 'Uma letra maiúscula',
    isMet: password => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'Uma letra minúscula',
    isMet: password => /[a-z]/.test(password),
  },
  {
    id: 'digit',
    label: 'Um número',
    isMet: password => /\d/.test(password),
  },
  {
    id: 'special',
    label: `Um caractere especial (${PASSWORD_SPECIAL_CHARACTERS})`,
    isMet: password => /[@$!%*?&]/.test(password),
  },
];

/** Whether every rule is satisfied. */
export function isPasswordValid(password: string): boolean {
  return PASSWORD_RULES.every(rule => rule.isMet(password));
}

/** The ids of the rules still unmet, in policy order. */
export function getUnmetPasswordRules(password: string): string[] {
  return PASSWORD_RULES.filter(rule => !rule.isMet(password)).map(rule => rule.id);
}
